import '../../styles/globals.css';
import type { AppProps } from 'next/app';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useState, useEffect } from 'react';
import { Hydrate } from 'react-query/hydration';
import { ReactQueryDevtools } from 'react-query/devtools';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { ThemeProvider } from '@mui/material/styles';
import { sellTheme } from 'themes/sell';
import { NextPage } from 'next';
import { ReactElement } from 'react-transition-group/node_modules/@types/react';
import TagManager from 'react-gtm-module';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import nextI18NextConfig from '../../next-i18next.config';

const environment =
  process.env.NODE_ENV === 'production' ? 'production' : 'stage';
const tagManagerArgs = {
  gtmId: `${process.env.NEXT_PUBLIC_TAGMANAGER_ID}`,
  dataLayer: {
    userMode: 'sell',
    country: `${process.env.NEXT_PUBLIC_COUNTRY}`,
    environment: environment,
    context: `${process.env.NEXT_PUBLIC_COUNTRY}_${environment}`,
    host: `${process.env.NEXT_PUBLIC_HOST}`,
  },
};

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactElement;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
declare global {
  interface Window {
    dataLayer: any[];
    BV: any;
  }
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      TagManager.initialize(tagManagerArgs);
    }
  }, []);
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = () => {
      if (window?.dataLayer) {
        window.dataLayer.push({ event: 'trackPageView' });
      }
    };
    //When the component is mounted, subscribe to router changes
    //and log those page views
    router.events.on('routeChangeComplete', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const [queryClient] = useState(() => new QueryClient());

  const getLayout = Component.getLayout
    ? (page: any) => (
        <ThemeProvider theme={sellTheme}>
          <QueryClientProvider client={queryClient}>
            {Component.getLayout && Component?.getLayout(page)}
          </QueryClientProvider>
        </ThemeProvider>
      )
    : (page: any) => <ThemeProvider theme={sellTheme}>{page}</ThemeProvider>;

  return getLayout(
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate
          state={(pageProps as { dehydratedState: unknown }).dehydratedState} //To avoid linter issues
        >
          <Component {...pageProps} />
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}

export default appWithTranslation(MyApp, nextI18NextConfig);
